import React, { useEffect, useState } from "react";
import { Button, Form, Modal } from "semantic-ui-react";
import useCategoriesTree, {
  invalidateCategoriesTree,
} from "../../../../hooks/useCategoriesTree";
import { updateCategory } from "../../../../services/categoriesApi";
import { ICON_NAMES, renderCategoryIcon } from "../../../../datas/categoryIcons";

const iconOptionContent = (name) => (
  <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
    <span style={{ display: "inline-block", width: 20, textAlign: "center" }}>
      {renderCategoryIcon(name, "#333", "lg")}
    </span>
    {name}
  </span>
);

const AVAILABILITY_OPTIONS = [
  { key: "always", text: "Toujours", value: "always" },
  { key: "lunch", text: "Midi (avant 18h)", value: "lunch" },
  { key: "dinner", text: "Soir (après 18h)", value: "dinner" },
];

function findChildrenInTree(nodes, id) {
  for (const n of nodes) {
    if (n._id === id) return n.children || [];
    const sub = findChildrenInTree(n.children || [], id);
    if (sub !== null) return sub;
  }
  return null;
}

const EditCategory = ({ open, onClose, target, setAppMessage }) => {
  const { tree } = useCategoriesTree();
  const [form, setForm] = useState({
    name: "",
    icon: "",
    iconColor: "#ffffff",
    availability: "always",
    defaultChildSlug: "",
    badgeIcon: "",
    badgeColor: "#ffd700",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!target) return;
    setForm({
      name: target.name || "",
      icon: target.icon || "",
      iconColor: target.iconColor || "#ffffff",
      availability: target.availability || "always",
      defaultChildSlug: target.defaultChildSlug || "",
      badgeIcon: target.badge?.icon || "",
      badgeColor: target.badge?.color || "#ffd700",
    });
  }, [target]);

  if (!target) return null;

  const isTopLevel = !target.parentId;
  const children = findChildrenInTree(tree, target._id) || [];
  const childOptions = [
    { key: "_none", text: "(aucune par défaut)", value: "" },
    ...children.map((c) => ({ key: c._id, text: c.name, value: c.slug })),
  ];
  const iconOptions = [
    { key: "_none", text: "(aucune)", value: "" },
    ...ICON_NAMES.map((n) => ({
      key: n,
      text: n,
      value: n,
      content: iconOptionContent(n),
    })),
  ];

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        icon: form.icon || null,
        iconColor: form.iconColor,
        defaultChildSlug: form.defaultChildSlug || null,
        badge: form.badgeIcon
          ? { icon: form.badgeIcon, color: form.badgeColor }
          : null,
      };
      if (isTopLevel) payload.availability = form.availability;
      await updateCategory(target._id, payload);
      invalidateCategoriesTree();
      onClose();
    } catch (e) {
      setAppMessage({
        success: false,
        message: e.response?.data?.message || "Erreur sauvegarde",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} size="small">
      <Modal.Header>Éditer : {target.name}</Modal.Header>
      <Modal.Content>
        <Form>
          <Form.Input
            label="Nom"
            value={form.name}
            onChange={(_, { value }) => setForm({ ...form, name: value })}
          />
          <Form.Field>
            <label>Slug (non modifiable)</label>
            <code
              style={{
                display: "inline-block",
                padding: "8px 12px",
                background: "#f5f5f5",
                color: "#333",
                borderRadius: 4,
              }}
            >
              {target.slug}
            </code>
          </Form.Field>
          <Form.Select
            label="Icône"
            options={iconOptions}
            value={form.icon}
            onChange={(_, { value }) => setForm({ ...form, icon: value })}
          />
          <Form.Input
            label="Couleur icône"
            type="color"
            value={form.iconColor}
            onChange={(_, { value }) => setForm({ ...form, iconColor: value })}
          />
          <Form.Select
            label="Badge superposé (optionnel)"
            options={iconOptions}
            value={form.badgeIcon}
            onChange={(_, { value }) => setForm({ ...form, badgeIcon: value })}
          />
          {form.badgeIcon && (
            <Form.Input
              label="Couleur du badge"
              type="color"
              value={form.badgeColor}
              onChange={(_, { value }) => setForm({ ...form, badgeColor: value })}
            />
          )}
          {isTopLevel && (
            <Form.Select
              label="Apparition"
              options={AVAILABILITY_OPTIONS}
              value={form.availability}
              onChange={(_, { value }) => setForm({ ...form, availability: value })}
            />
          )}
          {children.length > 0 && (
            <Form.Select
              label="Sous-catégorie par défaut"
              options={childOptions}
              value={form.defaultChildSlug}
              onChange={(_, { value }) => setForm({ ...form, defaultChildSlug: value })}
            />
          )}
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose}>Annuler</Button>
        <Button primary loading={saving} onClick={handleSave}>
          Enregistrer
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default EditCategory;
